import { useEffect, useMemo } from 'react';
import type { Class, ProjectKind, Sample } from '../data/types';
import { PoseOverlay } from './PoseOverlay';
import { useObjectUrl } from './useObjectUrl';
import './SampleModal.css';

export interface SampleModalProps {
  /** All classes in the project, in display order. */
  classes: Class[];
  /** Sample lists per class id. */
  samplesByClass: Record<string, Sample[]>;
  kind: ProjectKind;
  /** Currently focused class. */
  classId: string;
  /** Currently focused sample id within that class. */
  sampleId: string;
  /** Move focus to a different sample. The kid uses left/right arrows
   *  to walk through the same class, up/down to switch class. */
  onFocus: (next: { classId: string; sampleId: string }) => void;
  onClose: () => void;
  onDelete: (classId: string, sampleId: string) => void | Promise<void>;
  onReassign: (fromClassId: string, sampleId: string, toClassId: string) => void | Promise<void>;
}

/**
 * Modal sample inspector — TM-parity. Opens when the kid clicks a
 * thumbnail. Shows the sample large, with arrows to walk through the
 * class (left/right) and to switch focused class (up/down). The class
 * dropdown reassigns the sample, the trash icon deletes it, the X
 * closes. Keyboard: Esc closes, ←/→ walk samples, ↑/↓ walk classes.
 */
export function SampleModal({
  classes,
  samplesByClass,
  kind,
  classId,
  sampleId,
  onFocus,
  onClose,
  onDelete,
  onReassign,
}: SampleModalProps): JSX.Element | null {
  const cls = classes.find((c) => c.id === classId) ?? null;
  const samples = useMemo(() => samplesByClass[classId] ?? [], [samplesByClass, classId]);
  const sampleIndex = samples.findIndex((s) => s.id === sampleId);
  const sample = sampleIndex >= 0 ? samples[sampleIndex] : null;

  const url = useObjectUrl(sample?.blob ?? null);

  // Pre-compute class neighbours so the up/down arrows can wrap or
  // disable cleanly.
  const classIndex = classes.findIndex((c) => c.id === classId);

  const neighbour = useMemo(() => {
    function findFirstSampleId(targetClassId: string): string | null {
      return samplesByClass[targetClassId]?.[0]?.id ?? null;
    }
    function neighbourClassId(direction: -1 | 1): string | null {
      // Walk the class list looking for the next class with at least
      // one sample to focus. Wraps at the ends.
      if (classes.length === 0 || classIndex < 0) return null;
      for (let step = 1; step <= classes.length; step++) {
        const idx = (classIndex + direction * step + classes.length) % classes.length;
        const candidate = classes[idx];
        if (!candidate) continue;
        if (samplesByClass[candidate.id]?.length) return candidate.id;
      }
      return null;
    }
    return {
      prevSampleId: sampleIndex > 0 ? (samples[sampleIndex - 1]?.id ?? null) : null,
      nextSampleId:
        sampleIndex >= 0 && sampleIndex < samples.length - 1
          ? (samples[sampleIndex + 1]?.id ?? null)
          : null,
      prevClassId: neighbourClassId(-1),
      nextClassId: neighbourClassId(1),
      findFirstSampleId,
    };
  }, [classes, classIndex, samples, sampleIndex, samplesByClass]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === 'ArrowLeft' && neighbour.prevSampleId) {
        e.preventDefault();
        onFocus({ classId, sampleId: neighbour.prevSampleId });
      } else if (e.key === 'ArrowRight' && neighbour.nextSampleId) {
        e.preventDefault();
        onFocus({ classId, sampleId: neighbour.nextSampleId });
      } else if (e.key === 'ArrowUp' && neighbour.prevClassId) {
        e.preventDefault();
        const first = neighbour.findFirstSampleId(neighbour.prevClassId);
        if (first) onFocus({ classId: neighbour.prevClassId, sampleId: first });
      } else if (e.key === 'ArrowDown' && neighbour.nextClassId) {
        e.preventDefault();
        const first = neighbour.findFirstSampleId(neighbour.nextClassId);
        if (first) onFocus({ classId: neighbour.nextClassId, sampleId: first });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [classId, neighbour, onClose, onFocus]);

  if (!cls || !sample) return null;

  const handleReassign = async (e: React.ChangeEvent<HTMLSelectElement>): Promise<void> => {
    const target = e.target.value;
    if (target === classId) return;
    await onReassign(classId, sample.id, target);
    onFocus({ classId: target, sampleId: sample.id });
  };

  const handleDelete = async (): Promise<void> => {
    // Pre-compute the next focus *before* the delete so we don't flash
    // an empty modal in between renders.
    const nextSample =
      neighbour.nextSampleId ??
      neighbour.prevSampleId ??
      neighbour.findFirstSampleId(neighbour.nextClassId ?? '') ??
      neighbour.findFirstSampleId(neighbour.prevClassId ?? '');
    await onDelete(classId, sample.id);
    if (nextSample) {
      const nextClass =
        samples.length > 1 ? classId : (neighbour.nextClassId ?? neighbour.prevClassId ?? classId);
      onFocus({ classId: nextClass, sampleId: nextSample });
    } else {
      onClose();
    }
  };

  return (
    <div
      className="bl-sample-modal"
      role="dialog"
      aria-modal="true"
      aria-label={`Sample ${sampleIndex + 1} of ${samples.length} in ${cls.name}`}
    >
      <button
        type="button"
        className="bl-sample-modal__backdrop"
        onClick={onClose}
        aria-label="Close"
        tabIndex={-1}
      />
      <div className="bl-sample-modal__panel">
        <header className="bl-sample-modal__header">
          <div className="bl-sample-modal__class-nav">
            <button
              type="button"
              className="bl-sample-modal__arrow"
              onClick={() => {
                if (!neighbour.prevClassId) return;
                const first = neighbour.findFirstSampleId(neighbour.prevClassId);
                if (first) onFocus({ classId: neighbour.prevClassId, sampleId: first });
              }}
              disabled={!neighbour.prevClassId}
              aria-label="Previous class"
            >
              ↑
            </button>
            <button
              type="button"
              className="bl-sample-modal__arrow"
              onClick={() => {
                if (!neighbour.nextClassId) return;
                const first = neighbour.findFirstSampleId(neighbour.nextClassId);
                if (first) onFocus({ classId: neighbour.nextClassId, sampleId: first });
              }}
              disabled={!neighbour.nextClassId}
              aria-label="Next class"
            >
              ↓
            </button>
          </div>
          <h2 className="bl-sample-modal__title">{cls.name}</h2>
          <button
            type="button"
            className="bl-sample-modal__close"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="bl-sample-modal__media">
          {(kind === 'image' || kind === 'pose') && url && (
            <span className="bl-sample-modal__image-wrap">
              <img
                src={url}
                alt={`Sample ${sampleIndex + 1} of ${samples.length} in ${cls.name}`}
                className="bl-sample-modal__image"
              />
              {sample.poseKeypoints && sample.poseKeypoints.length > 0 && (
                <PoseOverlay keypoints={sample.poseKeypoints} dotRadius={0.018} />
              )}
            </span>
          )}
          {kind === 'audio' && url && (
            // A kid's own recording has no caption track to attach.
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <audio
              src={url}
              controls
              className="bl-sample-modal__audio"
              aria-label={`Sample ${sampleIndex + 1} of ${samples.length} in ${cls.name}`}
            />
          )}
        </div>

        <div className="bl-sample-modal__sample-nav">
          <button
            type="button"
            className="bl-sample-modal__arrow"
            onClick={() =>
              neighbour.prevSampleId && onFocus({ classId, sampleId: neighbour.prevSampleId })
            }
            disabled={!neighbour.prevSampleId}
            aria-label="Previous sample"
          >
            ‹
          </button>
          <span className="bl-sample-modal__counter" aria-live="polite">
            {sampleIndex + 1} / {samples.length}
          </span>
          <button
            type="button"
            className="bl-sample-modal__arrow"
            onClick={() =>
              neighbour.nextSampleId && onFocus({ classId, sampleId: neighbour.nextSampleId })
            }
            disabled={!neighbour.nextSampleId}
            aria-label="Next sample"
          >
            ›
          </button>
        </div>

        <footer className="bl-sample-modal__footer">
          <label className="bl-sample-modal__reassign">
            <span className="bl-sample-modal__reassign-label">Class</span>
            <select value={classId} onChange={(e) => void handleReassign(e)}>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            className="bl-sample-modal__delete"
            onClick={() => void handleDelete()}
            aria-label="Delete this sample"
          >
            🗑
          </button>
        </footer>
      </div>
    </div>
  );
}
